import React from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Heart } from "lucide-react";
import { promptLogin, promptRegister } from "@/lib/authRedirect";

// Custom event bus for wishlist changes (keeps other UI parts in sync).
export const wishlistBus = new EventTarget();

export function useWishlistIds() {
  const { user } = useAuth();
  const [ids, setIds] = React.useState([]);
  React.useEffect(() => {
    if (!user) { setIds([]); return; }
    const load = () => api.get("/wishlist/ids").then((r) => setIds(r.data)).catch(() => setIds([]));
    load();
    const h = () => load();
    wishlistBus.addEventListener("changed", h);
    return () => wishlistBus.removeEventListener("changed", h);
  }, [user]);
  return ids;
}

export default function WishlistButton({ schoolId, variant = "icon" }) {
  const { user } = useAuth();
  const nav = useNavigate();
  const ids = useWishlistIds();
  const active = ids.includes(schoolId);
  const [busy, setBusy] = React.useState(false);

  const toggle = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!user) { promptLogin(nav); return; }
    setBusy(true);
    try {
      const r = await api.post(`/wishlist/${schoolId}/toggle`);
      wishlistBus.dispatchEvent(new Event("changed"));
      if (r.data.wishlisted) {
        toast.success("Ditambahkan ke wishlist — Anda akan diberi tahu review baru.");
      } else {
        toast("Dihapus dari wishlist");
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Gagal");
    } finally { setBusy(false); }
  };

  if (variant === "wide") {
    return (
      <button
        onClick={toggle}
        disabled={busy}
        data-testid={`wishlist-toggle-${schoolId}`}
        className={
          "inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm border transition-colors duration-200 " +
          (active
            ? "bg-[#FEE2E2] text-[#B91C1C] border-[#FCA5A5]"
            : "bg-white text-[#4B5563] border-[#EAE7E0] hover:bg-[#FEF2F2] hover:text-[#B91C1C]")
        }
      >
        <Heart size={14} fill={active ? "#B91C1C" : "none"} />
        {active ? "Tersimpan" : "Simpan"}
      </button>
    );
  }
  return (
    <button
      onClick={toggle}
      disabled={busy}
      data-testid={`wishlist-toggle-${schoolId}`}
      className={
        "w-9 h-9 inline-flex items-center justify-center rounded-full border transition-colors duration-200 " +
        (active
          ? "bg-[#FEE2E2] text-[#B91C1C] border-[#FCA5A5]"
          : "bg-white text-[#4B5563] border-[#EAE7E0] hover:text-[#B91C1C] hover:border-[#FCA5A5]")
      }
      title={active ? "Hapus dari wishlist" : "Simpan ke wishlist"}
      aria-label="wishlist"
    >
      <Heart size={16} fill={active ? "#B91C1C" : "none"} />
    </button>
  );
}
